import type { Layer } from './synth';

/*
 * קטלוג הצלילים — **דאטה, בדיוק ברוח `src/content/`.**
 *
 * צליל חדש הוא רשומה כאן ולא פונקציה חדשה, מאותה סיבה שיחידה חדשה היא שורה
 * ב-UNIT_DEFS: ברגע שכל צליל הוא קוד, איזון של ערכת הסאונד הופך לעריכת קוד
 * בעשרים מקומות, ואף אחד לא נוגע בזה יותר.
 *
 * לכל רשומה יש שני חלקים:
 *   - **שכבות** — איך היא נשמעת (`synth.ts` בונה מהן צמתים).
 *   - **מדיניות** — תקרת קולות, חלון זמן מינימלי ווריאציית גובה. בלי החלק הזה
 *     קרב אמיתי מנגן ארבעים יריות באותו טיק, וזה נשמע כמו רעש לבן ומחניק את הדף.
 *
 * הקובץ הזה **אינו מייבא Web Audio ואינו יוצר שום צומת.** הוא דאטה בלבד, ולכן
 * הטסט יכול לאמת אותו בלי דפדפן.
 */

export interface SoundDef {
    readonly id: string;
    /** עוצמה יחסית של הצליל כולו, 0..1. */
    readonly gain: number;
    /** כמה עותקים של הצליל הזה מותר שיישמעו בו-זמנית. */
    readonly maxVoices: number;
    /** חלון מינימלי במילישניות בין שתי השמעות של אותו צליל. */
    readonly minGapMs: number;
    /** וריאציית גובה יחסית, 0..0.5. 0.12 = ±12% — שתי יריות לא נשמעות זהות. */
    readonly pitchJitter: number;
    readonly layers: readonly Layer[];
}

export const SOUND_DEFS = [
    // ---------------------------------------------------------------- ירי
    {
        // רובה: פצפוץ קצר וגבוה. מטח של שתיים, ולכן החלון קצר.
        id: 'shotLight',
        gain: 0.5,
        maxVoices: 4,
        minGapMs: 40,
        pitchJitter: 0.14,
        layers: [
            {
                source: 'noise',
                gain: 0.55,
                env: { attack: 0.001, decay: 0.035, sustain: 0.18, hold: 0, release: 0.05 },
                filter: { type: 'bandpass', frequency: 1900, endFrequency: 700, q: 1.1 },
            },
            {
                source: 'tone',
                wave: 'square',
                frequency: 240,
                endFrequency: 90,
                gain: 0.22,
                env: { attack: 0.001, decay: 0.04, sustain: 0.1, hold: 0, release: 0.04 },
                filter: { type: 'lowpass', frequency: 1200, q: 0.7 },
            },
        ],
    },
    {
        // תותח: בום נמוך עם קליק לוע. ארוך ונדיר יותר מהרובה.
        id: 'shotHeavy',
        gain: 0.62,
        maxVoices: 3,
        minGapMs: 70,
        pitchJitter: 0.09,
        layers: [
            {
                source: 'tone',
                wave: 'sine',
                frequency: 150,
                endFrequency: 42,
                gain: 0.65,
                env: { attack: 0.002, decay: 0.12, sustain: 0.3, hold: 0.02, release: 0.16 },
                filter: { type: 'lowpass', frequency: 520, q: 0.6 },
            },
            {
                source: 'noise',
                gain: 0.45,
                env: { attack: 0.001, decay: 0.09, sustain: 0.2, hold: 0, release: 0.12 },
                filter: { type: 'lowpass', frequency: 1400, endFrequency: 280, q: 0.8 },
            },
            {
                source: 'tone',
                wave: 'triangle',
                frequency: 900,
                endFrequency: 220,
                gain: 0.18,
                env: { attack: 0.001, decay: 0.03, sustain: 0, hold: 0, release: 0.02 },
            },
        ],
    },

    // ---------------------------------------------------------------- פגיעה
    {
        // קליע קל שנתקל: ריקושט חד וקצר.
        id: 'impactLight',
        gain: 0.34,
        maxVoices: 4,
        minGapMs: 45,
        pitchJitter: 0.2,
        layers: [
            {
                source: 'noise',
                gain: 0.5,
                env: { attack: 0.001, decay: 0.03, sustain: 0.12, hold: 0, release: 0.05 },
                filter: { type: 'bandpass', frequency: 3200, endFrequency: 1200, q: 2.4 },
            },
        ],
    },
    {
        // פגז שנוחת: פיצוץ בינוני.
        id: 'impactHeavy',
        gain: 0.6,
        maxVoices: 3,
        minGapMs: 60,
        pitchJitter: 0.12,
        layers: [
            {
                source: 'noise',
                gain: 0.6,
                env: { attack: 0.002, decay: 0.14, sustain: 0.28, hold: 0.02, release: 0.24 },
                filter: { type: 'lowpass', frequency: 1100, endFrequency: 160, q: 0.9 },
            },
            {
                source: 'tone',
                wave: 'sine',
                frequency: 95,
                endFrequency: 32,
                gain: 0.5,
                env: { attack: 0.004, decay: 0.16, sustain: 0.25, hold: 0.02, release: 0.2 },
            },
        ],
    },

    // ---------------------------------------------------------------- מוות
    {
        // יחידה שנהרסת: פיצוץ מלא עם שברים.
        id: 'deathUnit',
        gain: 0.68,
        maxVoices: 3,
        minGapMs: 70,
        pitchJitter: 0.12,
        layers: [
            {
                source: 'noise',
                gain: 0.65,
                env: { attack: 0.003, decay: 0.2, sustain: 0.3, hold: 0.04, release: 0.4 },
                filter: { type: 'lowpass', frequency: 1600, endFrequency: 130, q: 0.9 },
            },
            {
                source: 'tone',
                wave: 'sine',
                frequency: 78,
                endFrequency: 26,
                gain: 0.6,
                env: { attack: 0.005, decay: 0.24, sustain: 0.22, hold: 0.04, release: 0.3 },
            },
            {
                // שברים שנוחתים רגע אחרי — זה מה שהופך "בום" ל"הרס".
                source: 'noise',
                delay: 0.13,
                gain: 0.3,
                env: { attack: 0.01, decay: 0.16, sustain: 0.16, hold: 0.02, release: 0.3 },
                filter: { type: 'bandpass', frequency: 2400, endFrequency: 900, q: 1.4 },
            },
        ],
    },
    {
        // מבנה שקורס: עמוק וארוך יותר, עם רעד באדמה.
        id: 'deathBuilding',
        gain: 0.78,
        maxVoices: 2,
        minGapMs: 140,
        pitchJitter: 0.07,
        layers: [
            {
                source: 'noise',
                gain: 0.7,
                env: { attack: 0.004, decay: 0.32, sustain: 0.34, hold: 0.1, release: 0.7 },
                filter: { type: 'lowpass', frequency: 900, endFrequency: 90, q: 0.8 },
            },
            {
                source: 'tone',
                wave: 'sine',
                frequency: 52,
                endFrequency: 20,
                gain: 0.75,
                env: { attack: 0.008, decay: 0.4, sustain: 0.3, hold: 0.12, release: 0.6 },
            },
            {
                source: 'tone',
                wave: 'triangle',
                delay: 0.22,
                frequency: 40,
                endFrequency: 22,
                gain: 0.35,
                env: { attack: 0.05, decay: 0.3, sustain: 0.3, hold: 0.1, release: 0.5 },
                vibratoHz: 7,
                vibratoDepth: 6,
            },
        ],
    },

    // ---------------------------------------------------------------- פקודות
    {
        id: 'select',
        gain: 0.3,
        maxVoices: 2,
        minGapMs: 60,
        pitchJitter: 0.03,
        layers: [
            {
                source: 'tone',
                wave: 'triangle',
                frequency: 880,
                gain: 0.5,
                env: { attack: 0.002, decay: 0.04, sustain: 0.25, hold: 0.01, release: 0.05 },
                filter: { type: 'highpass', frequency: 300, q: 0.7 },
            },
        ],
    },
    {
        // תנועה: שני צלילים עולים — "קיבלתי, בדרך".
        id: 'moveOrder',
        gain: 0.32,
        maxVoices: 2,
        minGapMs: 70,
        pitchJitter: 0.03,
        layers: [
            {
                source: 'tone',
                wave: 'sine',
                frequency: 523,
                gain: 0.5,
                env: { attack: 0.003, decay: 0.03, sustain: 0.3, hold: 0.01, release: 0.05 },
            },
            {
                source: 'tone',
                wave: 'sine',
                delay: 0.06,
                frequency: 784,
                gain: 0.5,
                env: { attack: 0.003, decay: 0.04, sustain: 0.25, hold: 0.01, release: 0.07 },
            },
        ],
    },
    {
        // תקיפה: אותו מבנה, יורד ומחוספס — האוזן מבדילה בלי להסביר לה.
        id: 'attackOrder',
        gain: 0.34,
        maxVoices: 2,
        minGapMs: 70,
        pitchJitter: 0.03,
        layers: [
            {
                source: 'tone',
                wave: 'square',
                frequency: 392,
                gain: 0.4,
                env: { attack: 0.002, decay: 0.03, sustain: 0.3, hold: 0.01, release: 0.05 },
                filter: { type: 'lowpass', frequency: 2200, q: 0.8 },
            },
            {
                source: 'tone',
                wave: 'square',
                delay: 0.06,
                frequency: 262,
                gain: 0.4,
                env: { attack: 0.002, decay: 0.05, sustain: 0.25, hold: 0.01, release: 0.08 },
                filter: { type: 'lowpass', frequency: 1800, q: 0.8 },
            },
        ],
    },

    // ---------------------------------------------------------------- ייצור
    {
        // "המבנה מוכן" — שלושה צלילים עולים, הכרזה ולא קליק.
        id: 'buildingReady',
        gain: 0.38,
        maxVoices: 1,
        minGapMs: 300,
        pitchJitter: 0,
        layers: [
            {
                source: 'tone',
                wave: 'triangle',
                frequency: 392,
                gain: 0.45,
                env: { attack: 0.006, decay: 0.05, sustain: 0.5, hold: 0.05, release: 0.1 },
            },
            {
                source: 'tone',
                wave: 'triangle',
                delay: 0.11,
                frequency: 523,
                gain: 0.45,
                env: { attack: 0.006, decay: 0.05, sustain: 0.5, hold: 0.05, release: 0.1 },
            },
            {
                source: 'tone',
                wave: 'triangle',
                delay: 0.22,
                frequency: 659,
                gain: 0.45,
                env: { attack: 0.006, decay: 0.06, sustain: 0.45, hold: 0.08, release: 0.22 },
            },
        ],
    },
    {
        // "היחידה מוכנה" — קצר יותר, קורה הרבה יותר.
        id: 'unitReady',
        gain: 0.3,
        maxVoices: 2,
        minGapMs: 200,
        pitchJitter: 0.02,
        layers: [
            {
                source: 'tone',
                wave: 'triangle',
                frequency: 523,
                gain: 0.45,
                env: { attack: 0.004, decay: 0.04, sustain: 0.4, hold: 0.02, release: 0.08 },
            },
            {
                source: 'tone',
                wave: 'triangle',
                delay: 0.08,
                frequency: 698,
                gain: 0.45,
                env: { attack: 0.004, decay: 0.05, sustain: 0.35, hold: 0.03, release: 0.12 },
            },
        ],
    },
    {
        // מבנה הוצב: חבטה של בטון על אדמה.
        id: 'placed',
        gain: 0.5,
        maxVoices: 2,
        minGapMs: 120,
        pitchJitter: 0.06,
        layers: [
            {
                source: 'tone',
                wave: 'sine',
                frequency: 130,
                endFrequency: 55,
                gain: 0.6,
                env: { attack: 0.003, decay: 0.1, sustain: 0.25, hold: 0.02, release: 0.16 },
            },
            {
                source: 'noise',
                gain: 0.35,
                env: { attack: 0.002, decay: 0.06, sustain: 0.2, hold: 0, release: 0.12 },
                filter: { type: 'lowpass', frequency: 800, endFrequency: 240, q: 0.8 },
            },
        ],
    },
    {
        // אשראי נכנס. **עדין בכוונה** — הקוצר פורק כל כמה שניות לאורך כל המשחק,
        // וצליל שנשמע "נחמד" בפעם הראשונה הופך לעינוי בפעם החמישים.
        id: 'credits',
        gain: 0.12,
        maxVoices: 2,
        minGapMs: 90,
        pitchJitter: 0.05,
        layers: [
            {
                source: 'tone',
                wave: 'sine',
                frequency: 1568,
                gain: 0.4,
                env: { attack: 0.001, decay: 0.03, sustain: 0.12, hold: 0, release: 0.04 },
                filter: { type: 'highpass', frequency: 800, q: 0.7 },
            },
        ],
    },

    // ---------------------------------------------------------------- סיום
    {
        id: 'victory',
        gain: 0.45,
        maxVoices: 1,
        minGapMs: 2000,
        pitchJitter: 0,
        layers: [
            {
                source: 'tone',
                wave: 'triangle',
                frequency: 523,
                gain: 0.45,
                env: { attack: 0.008, decay: 0.06, sustain: 0.5, hold: 0.06, release: 0.12 },
            },
            {
                source: 'tone',
                wave: 'triangle',
                delay: 0.14,
                frequency: 659,
                gain: 0.45,
                env: { attack: 0.008, decay: 0.06, sustain: 0.5, hold: 0.06, release: 0.12 },
            },
            {
                source: 'tone',
                wave: 'triangle',
                delay: 0.28,
                frequency: 784,
                gain: 0.45,
                env: { attack: 0.008, decay: 0.06, sustain: 0.5, hold: 0.06, release: 0.12 },
            },
            {
                source: 'tone',
                wave: 'triangle',
                delay: 0.42,
                frequency: 1046,
                gain: 0.5,
                env: { attack: 0.008, decay: 0.1, sustain: 0.5, hold: 0.25, release: 0.5 },
            },
            {
                source: 'tone',
                wave: 'sine',
                delay: 0.42,
                frequency: 261,
                gain: 0.35,
                env: { attack: 0.02, decay: 0.15, sustain: 0.45, hold: 0.25, release: 0.6 },
            },
        ],
    },
    {
        // הפסד: יורד, מתנדנד ומתפוגג — צפירה שנכבית.
        id: 'defeat',
        gain: 0.45,
        maxVoices: 1,
        minGapMs: 2000,
        pitchJitter: 0,
        layers: [
            {
                source: 'tone',
                wave: 'sawtooth',
                frequency: 220,
                endFrequency: 62,
                gain: 0.4,
                env: { attack: 0.05, decay: 0.4, sustain: 0.45, hold: 0.35, release: 0.9 },
                filter: { type: 'lowpass', frequency: 1400, endFrequency: 300, q: 0.9 },
                vibratoHz: 5,
                vibratoDepth: 8,
            },
            {
                source: 'tone',
                wave: 'sine',
                frequency: 110,
                endFrequency: 40,
                gain: 0.45,
                env: { attack: 0.06, decay: 0.5, sustain: 0.4, hold: 0.3, release: 1 },
            },
        ],
    },
] as const satisfies readonly SoundDef[];

export type SoundId = (typeof SOUND_DEFS)[number]['id'];

/*
 * אותה טבלה בדיוק, בטיפוס הרחב.
 *
 * ‏`as const` נחוץ כדי ש-`SoundId` ייגזר מהמזהים ומזהה שגוי ייפול בקומפילציה —
 * אבל הוא גם מצמצם כל רשומה לטיפוס המדויק שלה, וכך שכבה בלי `filter` "לא מכירה"
 * את השדה בכלל. מי שרוצה לעבור על השכבות עובר כאן.
 */
export const SOUNDS: readonly SoundDef[] = SOUND_DEFS;

export const SOUND_IDS: readonly SoundId[] = SOUND_DEFS.map((def) => def.id);

export const SOUND_BY_ID = Object.fromEntries(SOUNDS.map((def) => [def.id, def])) as Readonly<
    Record<SoundId, SoundDef>
>;

export function soundDef(id: SoundId): SoundDef {
    return SOUND_BY_ID[id];
}
